import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

export function PasswordResetPage() {
  const navigate = useNavigate();
  const { resetPassword, session } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // User must have a session from the reset link to be on this page
    if (!session) {
      navigate('/login');
    }
  }, [session, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error } = await resetPassword(newPassword);

    if (error) {
      setError(error);
    } else {
      setSuccess('Password reset successfully! Redirecting...');
      setTimeout(() => navigate('/'), 2000);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-warm-50 dark:bg-gray-950 flex flex-col items-center justify-center px-6">
      {/* Logo / Branding */}
      <div className="text-center mb-10">
        <div className="text-5xl mb-3">🍽️</div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
          Sabbath Lunch
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
          Reset Your Password
        </p>
      </div>

      {/* Form Container */}
      <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-3xl shadow-sm border border-warm-100 dark:border-gray-800 p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Enter your new password below.
          </p>

          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
            required
          />

          <Input
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
            required
          />

          {error && (
            <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 px-3 py-2.5 text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {success && (
            <div className="rounded-xl bg-green-50 dark:bg-green-950 border border-green-100 dark:border-green-900 px-3 py-2.5 text-sm text-green-600 dark:text-green-400">
              {success}
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            size="lg"
            loading={loading}
            className="mt-6"
          >
            Reset Password
          </Button>
        </form>
      </div>
    </div>
  );
}
